import _map from 'lodash/map';
import queryString from 'query-string';

import { AttributionData, ExtraData } from './interfaces';

import * as Adjust from './mmp/adjust';
import * as AppsFlyer from './mmp/appsflyer';

import * as Facebook from './pixel/facebook';
import * as Google from './pixel/google';
import * as TikTok from './pixel/tiktok';
import * as Snapchat from './pixel/snapchat';

import { decode } from './utils';

const attribution: AttributionData = {
  source: undefined,
  c: undefined,
  c_id: undefined,
  adset: undefined,
  adset_id: undefined,
  ad: undefined,
  ad_id: undefined,
};

const extra: ExtraData = {
  site_url: window.location.protocol + '//' + window.location.host + window.location.pathname,

  web_client_user_agent: navigator.userAgent,

  web_external_id: undefined,

  web_ga_measurement_id: undefined,
  web_ga_client_id: undefined,
  web_ga_gclid: undefined,

  web_facebook_pixel_id: undefined,
  web_facebook_browser_id: undefined,
  web_facebook_click_id: undefined,

  web_tiktok_pixel_id: undefined,
  web_tiktok_click_id: undefined,

  web_snapchat_pixel_id: undefined,
  web_snapchat_client_id: undefined,
};

let onChange: (system: System, data: any) => void;

type System = 'facebook' | 'google' | 'tiktok' | 'snapchat';

interface ConfigOptions {
  systems?: Array<System>;
  timeout?: number;
  onChange?: (system: System, data: any) => void;
}

export default {
  init(options: ConfigOptions) {
    options = options || {};

    const systems = options.systems || ['facebook', 'google', 'tiktok', 'snapchat'];
    const timeout = options.timeout || 4000;

    onChange = options.onChange || (() => { });

    const search = queryString.parse(window.location.search);

    attribution.source = String(search.utm_source || '') || undefined;
    attribution.c = decode(String(search.campaign_name || '')) || undefined;
    attribution.c_id = String(search.campaign_id || '') || undefined;
    attribution.adset = decode(String(search.adset_name || '')) || undefined;
    attribution.adset_id = String(search.adset_id || '') || undefined;
    attribution.ad = decode(String(search.ad_name || '')) || undefined;
    attribution.ad_id = String(search.ad_id || '') || undefined;

    const p = _map(systems, s => {
      switch (s) {
        case 'facebook':
          return Facebook.collectParams({
            onChange: (m) => {
              Object.assign(extra, {
                web_facebook_pixel_id: m.pixel_id,
                web_facebook_browser_id: m.browser_id,
                web_facebook_click_id: m.click_id,
                web_external_id: m.external_id,
              });
              onChange(s, m);
            }
          });
        case 'google':
          return Google.collectParams({
            onChange: (m) => {
              Object.assign(extra, {
                web_ga_measurement_id: m.measurement_id,
                web_ga_client_id: m.client_id,
                web_ga_gclid: m.gclid,
              });
              onChange(s, m);
            }
          });
        case 'tiktok':
          return TikTok.collectParams({
            onChange: (m) => {
              Object.assign(extra, {
                web_tiktok_pixel_id: m.pixel_id,
                web_tiktok_click_id: m.click_id,
              });
              onChange(s, m);
            }
          });
        case 'snapchat':
          return Snapchat.collectParams({
            onChange: (m) => {
              Object.assign(extra, {
                web_snapchat_pixel_id: m.pixel_id,
                web_snapchat_client_id: m.client_id,
                web_snapchat_click_id: m.click_id,
              });
              onChange(s, m);
            }
          });
        default:
          return Promise.reject();
      }
    });

    return Promise.race([
      Promise.all(p),
      new Promise(resolve => {
        setTimeout(resolve, timeout);
      }),
    ]);
  },

  trackAdjustImpression(tracker: string) {
    return Adjust.trackImpression(attribution, tracker);
  },

  createAppsFlyerLink(url: string, deeplinkParams = {}) {
    return queryString.stringifyUrl({
      url,
      query: {
        ...AppsFlyer.mapAttribution(attribution),
        ...extra,
        ...deeplinkParams,
      },
    });
  },

  createAdjustLink(url: string, tracker: string, deeplinkParams = {}) {
    return queryString.stringifyUrl({
      url,
      query: {
        ...Adjust.mapAttribution(attribution, tracker),
        ...extra,
        ...deeplinkParams,
      },
    });
  },

  getAttribution() {
    return {
      ...attribution,
      ...extra,
    };
  },
}
